import {AlarmOutlined} from '@mui/icons-material';
import {
  Alert,
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  FormHelperText,
  Paper,
  Tooltip,
} from '@mui/material';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useAppSelector} from '../../hooks/redux';
import useShiftSelect, {isShiftClick} from '../../hooks/useShiftSelect';
import {
  AlarmModuleNode,
  AlarmModuleNodeListResponse,
} from '../../interfaces/AlarmModuleNode';
import reduxActions from '../../redux/actions';
import DataGrid, {DataGridColumn, DataGridRef} from '../common/DataGrid';
interface Props {
  formik?: any;
  preSelected?: number[];
  onSelectItems?: (ids: number[]) => void;
}
const AlarmUpdateStatusAlarmList = ({
  onSelectItems,
  formik,
  preSelected,
}: Props) => {
  const dataGridRef = useRef<DataGridRef>(null);
  const zones = useAppSelector(({assets}) => assets.zones);
  const reduxDispatch = useDispatch();
  const activationReason = useAppSelector(
    ({assets}) => assets.activationReason
  );
  const [fetchedAlarmModuleData, setFetchedAlarmModuleData] =
    useState<AlarmModuleNodeListResponse>();
  const [fetchedAlarmModuleErrors, setFetchedAlarmModuleErrors] = useState<
    string[]
  >([]);
  const [
    fetchedAlarmModuleDataInProgress,
    setFetchedAlarmModuleDataInProgress,
  ] = useState(false);
  useEffect(() => {
    fetchAlarmModules();
  }, []);

  const fetchAlarmModules = async () => {
    setFetchedAlarmModuleDataInProgress(true);
    setFetchedAlarmModuleErrors([]);

    try {
      const params = {};

      const resp = await API.get<AlarmModuleNodeListResponse>(
        `${apiBaseUrl}/alarm-module`,
        {params}
      );

      setFetchedAlarmModuleData(resp.data);
      reduxActions.assets.setAssets(reduxDispatch, {
        alarm_modules: resp.data.items,
      });
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_6d487d07');
      setFetchedAlarmModuleErrors(messages);
    } finally {
      setFetchedAlarmModuleDataInProgress(false);
    }
  };

  const rows = useMemo(() => {
    return fetchedAlarmModuleData?.items?.filter(
      (it) => it.status !== 'inactive'
    );
  }, [fetchedAlarmModuleData]);

  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  useEffect(() => {
    if (onSelectItems) {
      setSelectedItems(preSelected ?? []);
    }
  }, [preSelected]);

  useEffect(() => {
    if (onSelectItems) {
      onSelectItems(selectedItems);
    }
  }, [selectedItems]);

  useEffect(() => {
    if (formik.values.module === 'all') {
      selectAll();
    }
  }, [formik.values.module]);
  useEffect(() => {
    if (formik.values.module === 'all') {
      selectAll();
    }
  });

  const selectedRows = useMemo(
    () => rows?.filter((i) => selectedItems.includes(i.id)),
    [rows, selectedItems]
  );

  const selectedAll = useMemo(
    () => rows?.length === selectedRows?.length,
    [rows, selectedRows]
  );

  const getNextSelectedIds = useShiftSelect<number>();

  const toggleSelectItem = (
    id: number,
    shiftKey: boolean,
    visibleIds: number[]
  ) => {
    const nextSelectedIds = getNextSelectedIds({
      id,
      visibleIds,
      selectedIds: selectedItems ?? [],
      shiftKey,
    });
    setSelectedItems(nextSelectedIds);
  };

  const selectAll = () => {
    setSelectedItems((rows ?? []).map((i) => i.id));
  };
  const unselectAll = () => {
    setSelectedItems([]);
  };
  const toggleSelectAllItems = () => {
    if (selectedItems.length >= (rows?.length ?? 0)) {
      unselectAll();
    } else {
      selectAll();
    }
  };
  const columns: DataGridColumn<AlarmModuleNode>[] = [
    {
      field: 'select',
      type: 'select',
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows?.length === 0}
          checked={selectedItems.length > 0 && selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row, visibleRows}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(row.id)}
          onChange={(event) =>
            toggleSelectItem(
              row.id,
              isShiftClick(event),
              visibleRows.map((visibleRow) => visibleRow.id)
            )
          }
        />
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 60,
      resizeable: true,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 120,
      resizeable: true,
    },
    {
      field: 'type',
      headerName: 'Alarm',
      width: 80,
      resizeable: true,
      sortable: true,
      renderHeader: () => {
        return <AlarmOutlined />;
      },
      renderCell: ({row}) => {
        if (row.alarm) {
          return (
            <Tooltip title="Alarm">
              <AlarmOutlined color="error" />
            </Tooltip>
          );
        } else if (row.warning) {
          return (
            <Tooltip title="Warning">
              <AlarmOutlined color="warning" />
            </Tooltip>
          );
        } else {
          return <></>;
        }
      },
    },
    {
      field: 'reason',
      headerName: 'Reason',
      width: 80,
      resizeable: true,
      renderCell: ({row}) => {
        if (row.alarm) {
          return (
            <>
              {activationReason?.find((o) => o.key === row.alarm_reason)
                ?.label ?? ''}
            </>
          );
        } else if (row.warning) {
          return (
            <>
              {activationReason?.find((o) => o.key === row.warning_reason)
                ?.label ?? ''}
            </>
          );
        } else {
          return <></>;
        }
      },
    },
    {
      field: 'note',
      headerName: 'Note',
      width: 80,
      resizeable: true,
      renderCell: ({row}) => {
        if (row.alarm) {
          return <>{row.alarm_reason_note}</>;
        } else if (row.warning) {
          return <>{row.warning_reason_note}</>;
        } else {
          return <></>;
        }
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 80,
      sortable: true,
      renderCell: ({row}) => {
        return <Box textTransform="capitalize">{row.status}</Box>;
      },
    },
    {
      field: 'zone_id',
      headerName: 'Section',
      width: 100,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => {
        return (
          <Box whiteSpace="nowrap">
            {zones?.find((it) => it.id === row.zone_id)?.name}
          </Box>
        );
      },
    },
    {
      field: 'mac_address',
      headerName: 'Mac Address',
      width: 150,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => {
        return row.mac_address;
      },
    },
  ];
  return (
    <Paper sx={{p: 2, px: 3}}>
      <Box fontSize={16} mb={2}>
        Select the alarm modules you want apply:
      </Box>
      {fetchedAlarmModuleErrors.map((error, idx) => (
        <Alert
          key={`error-es-${idx}`}
          severity="error"
          onClose={() => setFetchedAlarmModuleErrors([])}
        >
          {error}
        </Alert>
      ))}
      <FormHelperText
        error={!!formik.touched.alarm_modules && !!formik.errors.alarm_modules}
      >
        {formik.touched.alarm_modules && formik.errors.alarm_modules}
      </FormHelperText>
      <DataGrid
        ref={dataGridRef}
        rows={rows ?? []}
        loading={fetchedAlarmModuleDataInProgress}
        columns={columns}
        size="small"
        pagination
        sx={{
          td: {
            padding: '10px 16px',
          },
        }}
      />
      <Backdrop
        open={fetchedAlarmModuleDataInProgress}
        sx={{position: 'absolute'}}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};

export default AlarmUpdateStatusAlarmList;
